"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.JobStatusProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const JobStatusModal_1 = require("../../components/jobStatusModal/JobStatusModal");
const jobStatusApi_1 = require("../../services/jobStatusApi");
const useLocale_1 = require("../../hooks/useLocale/useLocale");
const JobStatusContext_1 = require("./JobStatusContext");
/**
 * Global JobStatusProvider component
 *
 * This provider manages a single JobStatusModal instance that persists
 * across component unmounts and modal-to-modal transitions. It provides
 * a context for components to trigger job tracking without managing
 * their own modal instances.
 *
 * The provider renders the JobStatusModal at the top level of the component
 * tree, ensuring it's never unmounted when individual components are destroyed.
 */
const JobStatusProvider = ({ children }) => {
    const [state, setState] = (0, react_1.useState)(JobStatusContext_1.INITIAL_STATE);
    const jobStatusApi = (0, jobStatusApi_1.useJobStatusApi)();
    const { formatMessage } = (0, useLocale_1.useLocale)();
    /**
     * Start tracking a job with the global modal
     */
    const startJob = (0, react_1.useCallback)(async (props) => {
        const { jobId, title = JobStatusContext_1.DEFAULT_MODAL_CONFIG.title, description = JobStatusContext_1.DEFAULT_MODAL_CONFIG.description, autoClose = JobStatusContext_1.DEFAULT_MODAL_CONFIG.autoClose, autoCloseDelay = JobStatusContext_1.DEFAULT_MODAL_CONFIG.autoCloseDelay, closeable = JobStatusContext_1.DEFAULT_MODAL_CONFIG.closeable, showDetails = JobStatusContext_1.DEFAULT_MODAL_CONFIG.showDetails, onComplete = JobStatusContext_1.DEFAULT_MODAL_CONFIG.onComplete, onError = JobStatusContext_1.DEFAULT_MODAL_CONFIG.onError, showItemCount = JobStatusContext_1.DEFAULT_MODAL_CONFIG.showItemCount, customCompleteMessage = JobStatusContext_1.DEFAULT_MODAL_CONFIG.customCompleteMessage, customFailedMessage = JobStatusContext_1.DEFAULT_MODAL_CONFIG.customFailedMessage, customActiveMessage = JobStatusContext_1.DEFAULT_MODAL_CONFIG.customActiveMessage, } = props;
        // Check if this is an empty job (0/0) and show toast instead of modal
        try {
            const jobStatus = await jobStatusApi.getJobStatus(jobId);
            if (jobStatus && jobStatus.progress && jobStatus.progress.total === 0) {
                if (onComplete) {
                    onComplete(jobStatus);
                }
                return;
            }
        }
        catch (error) {
            // If we can't fetch job status, proceed with modal (job might not be created yet)
            console.warn('Could not check job status for empty detection:', error);
        }
        setState((prev) => ({
            ...prev,
            isModalOpen: true,
            currentJobId: jobId,
            modalConfig: {
                title,
                description,
                autoClose,
                autoCloseDelay,
                closeable,
                showDetails,
                onComplete,
                onError,
                showItemCount,
                customCompleteMessage,
                customFailedMessage,
                customActiveMessage,
            },
            lastJobResult: null,
            lastError: null,
        }));
    }, []);
    /**
     * Close the modal manually
     */
    const closeModal = (0, react_1.useCallback)(() => {
        setState((prev) => ({
            ...prev,
            isModalOpen: false,
            currentJobId: null,
            modalConfig: JobStatusContext_1.DEFAULT_MODAL_CONFIG,
        }));
    }, []);
    /**
     * Handle job completion
     */
    const handleJobComplete = (0, react_1.useCallback)((jobStatus) => {
        setState((prev) => ({
            ...prev,
            lastJobResult: jobStatus,
            lastError: null,
        }));
        // Call custom completion handler if provided
        if (state.modalConfig.onComplete) {
            state.modalConfig.onComplete(jobStatus);
        }
        // If auto-close is disabled, we'll let the modal handle its own closing
        if (!state.modalConfig.autoClose) {
            // Modal will show completion state and allow manual close
            return;
        }
        // If auto-close is enabled, the modal will close itself after delay
        // We'll clean up our state after the modal closes
        setTimeout(() => {
            setState((prev) => ({
                ...prev,
                isModalOpen: false,
                currentJobId: null,
                modalConfig: JobStatusContext_1.DEFAULT_MODAL_CONFIG,
            }));
        }, state.modalConfig.autoCloseDelay + 100); // Small buffer after modal closes
    }, [state.modalConfig.autoClose, state.modalConfig.autoCloseDelay, state.modalConfig.onComplete]);
    /**
     * Handle job error
     */
    const handleJobError = (0, react_1.useCallback)((error) => {
        setState((prev) => ({
            ...prev,
            lastError: error,
            lastJobResult: null,
        }));
        // Call custom error handler if provided
        if (state.modalConfig.onError) {
            state.modalConfig.onError(error);
        }
    }, [state.modalConfig.onError]);
    /**
     * Clear last results
     */
    const clearResults = (0, react_1.useCallback)(() => {
        setState((prev) => ({
            ...prev,
            lastJobResult: null,
            lastError: null,
        }));
    }, []);
    // Prepare context value
    const actions = {
        startJob,
        closeModal,
        handleJobComplete,
        handleJobError,
        clearResults,
    };
    const contextValue = {
        state,
        actions,
    };
    return ((0, jsx_runtime_1.jsxs)(JobStatusContext_1.JobStatusContext.Provider, { value: contextValue, children: [children, (0, jsx_runtime_1.jsx)(JobStatusModal_1.JobStatusModal, { open: state.isModalOpen, jobId: state.currentJobId || '', title: state.modalConfig.title, onClose: closeModal, onComplete: handleJobComplete, onError: handleJobError, autoClose: state.modalConfig.autoClose, autoCloseDelay: state.modalConfig.autoCloseDelay, closeable: state.modalConfig.closeable, showItemCount: state.modalConfig.showItemCount, customCompleteMessage: state.modalConfig.customCompleteMessage, customFailedMessage: state.modalConfig.customFailedMessage, customActiveMessage: state.modalConfig.customActiveMessage })] }));
};
exports.JobStatusProvider = JobStatusProvider;
